(() => {
  if (customElements.get('zenpaaw-cart-drawer')) return;

  class ZenPaawCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeElement = null;
      this.busy = false;
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('keyup', (event) => { if (event.code === 'Escape') this.close(); });
    }
    connectedCallback() { this.updateHeaderCountFromDrawer(); }
    getSectionsToRender() { return [{ id: 'zenpaaw-cart-drawer' }]; }
    setActiveElement(element) { this.activeElement = element || document.activeElement; }
    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      this.classList.add('active');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('zpcart-open');
      requestAnimationFrame(() => this.querySelector('.zpcart-panel')?.focus({ preventScroll: true }));
    }
    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('zpcart-open');
      if (this.activeElement && document.contains(this.activeElement)) {
        try { this.activeElement.focus({ preventScroll: true }); } catch (e) {}
      }
    }
    handleClick(event) {
      if (event.target.closest('[data-zpcart-close]')) { event.preventDefault(); this.close(); return; }
      const button = event.target.closest('[data-zpcart-action]');
      if (!button || this.busy) return;
      event.preventDefault();
      const line = Number(button.dataset.line);
      const action = button.dataset.zpcartAction;
      const qty = Number(this.querySelector(`[data-zpcart-qty="${line}"]`)?.textContent || 1);
      let next = qty;
      if (action === 'plus') next = qty + 1;
      if (action === 'minus') next = Math.max(0, qty - 1);
      if (action === 'remove') next = 0;
      this.changeLine(line, next);
    }
    async changeLine(line, quantity) {
      if (!line || this.busy) return;
      this.busy = true;
      const item = this.querySelector(`[data-zpcart-line-item="${line}"]`);
      item?.classList.add('is-loading');
      this.showError('');
      try {
        const response = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity })
        });
        const cart = await response.json();
        if (!response.ok || cart.errors) throw new Error(cart.description || cart.message || cart.errors || 'Could not update cart.');
        await this.refresh(false, cart.item_count);
      } catch (error) {
        this.showError(error.message || 'Could not update your cart. Please try again.');
        item?.classList.remove('is-loading');
      } finally { this.busy = false; }
    }
    async refresh(openAfter = false, knownCount = null) {
      try {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}?section_id=zenpaaw-cart-drawer`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Could not refresh cart.');
        this.replaceFromHTML(await response.text());
        if (knownCount !== null) this.updateHeaderCount(knownCount); else await this.fetchAndUpdateHeaderCount();
        if (openAfter) this.open();
      } catch (error) { this.showError(error.message || 'Could not refresh your cart.'); }
    }
    replaceFromHTML(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('zenpaaw-cart-drawer');
      if (!fresh) throw new Error('Cart drawer response was incomplete.');
      const wasOpen = this.classList.contains('active');
      this.innerHTML = fresh.innerHTML;
      this.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
      if (wasOpen) this.classList.add('active');
      this.updateHeaderCountFromDrawer();
    }
    renderContents(state) {
      const html = state?.sections?.['zenpaaw-cart-drawer'];
      if (html) {
        try { this.replaceFromHTML(html); this.fetchAndUpdateHeaderCount(); this.open(); return; } catch (e) {}
      }
      this.refresh(true);
    }
    showError(message) { const box = this.querySelector('[data-zpcart-error]'); if (box) box.textContent = message || ''; }
    updateHeaderCountFromDrawer() {
      const match = (this.querySelector('.zpcart-count')?.textContent || '').match(/\d+/);
      if (match) this.updateHeaderCount(Number(match[0])); else if (this.classList.contains('is-empty')) this.updateHeaderCount(0);
    }
    updateHeaderCount(count) {
      document.querySelectorAll('.zph-count').forEach((node) => node.textContent = count);
      document.querySelectorAll('.zph-cart').forEach((node) => node.setAttribute('aria-label', `Cart with ${count} ${count === 1 ? 'item' : 'items'}`));
    }
    async fetchAndUpdateHeaderCount() {
      try {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
        if (response.ok) this.updateHeaderCount((await response.json()).item_count || 0);
      } catch (e) {}
    }
  }

  customElements.define('zenpaaw-cart-drawer', ZenPaawCartDrawer);

  if (!window.__zenpaawCartEventsBound) {
    window.__zenpaawCartEventsBound = true;
    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/cart/add')) return;
      const drawer = document.querySelector('zenpaaw-cart-drawer');
      if (!drawer) return;
      event.preventDefault(); event.stopImmediatePropagation();
      const button = form.querySelector('[type="submit"]');
      if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); }
      drawer.showError('');
      try {
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }, body: new FormData(form)
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add this item to your cart.');
        const modal = form.closest('quick-add-modal'); if (modal && typeof modal.hide === 'function') modal.hide(true);
        await drawer.refresh(true);
      } catch (error) {
        drawer.showError(error.message || 'Could not add this item to your cart.'); drawer.open(button || form);
      } finally {
        if (button) { button.disabled = false; button.removeAttribute('aria-busy'); }
      }
    }, true);
  }
})();